import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, List } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../services/api';
import './css/searchComponent.css';

const { Search } = Input;
const { Option } = Select;

const isMobile = window.innerWidth <= 768;
const mobileSearchClass = isMobile ? 'custom-search-mobile' : '';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'] });
  const [searchText, setSearchText] = useState('');
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [form] = Form.useForm();

  const fetchUsers = async (page = 1, search = '', pageSize = 5) => {
    setLoading(true);
    try {
      const response = await api.get('/users', {
        params: { page, limit: pageSize, search }
      });
      setUsers(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, current: page, pageSize }));
    } catch (error) {
      message.error('Erro ao carregar usuários');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleTableChange = (pag) => {
    const newPage = pag.pageSize !== pagination.pageSize ? 1 : pag.current;
    fetchUsers(newPage, searchText, pag.pageSize);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchUsers(1, value, 5);
  };

  const showModal = (user = null) => {
    setEditingUser(user);
    setModalVisible(true);
    setAvailableTeams([]);
    setSelectedTeam(null);
    if (user) {
      form.setFieldsValue(user);
    } else {
      form.resetFields();
    }
  };

  const searchTeamsByPhone = async (phone) => {
    if (!phone || phone.length < 10) {
      setAvailableTeams([]);
      return;
    }
    
    try {
      const response = await api.get(`/customer-search/search-by-phone/${phone}`);
      setAvailableTeams(response.data);
      if (response.data.length === 1) {
        setSelectedTeam(response.data[0].id);
        form.setFieldValue('customer_id', response.data[0].id);
      }
    } catch (error) {
      message.error('Erro ao buscar times');
    }
  };

  const handleTeamSelect = (teamId) => {
    setSelectedTeam(teamId);
    form.setFieldValue('customer_id', teamId);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, values);
        message.success('Usuário atualizado com sucesso!');
      } else {
        await api.post('/users', values);
        message.success('Usuário criado com sucesso!');
      }
      setModalVisible(false);
      fetchUsers(pagination.current, searchText);
    } catch (error) {
      message.error(error.response?.data?.message || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      message.success('Usuário inativado com sucesso!');
      fetchUsers(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao inativar usuário');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => a.id - b.id },
    { title: 'Usuário', dataIndex: 'username', key: 'username', sorter: (a, b) => a.username?.localeCompare(b.username) },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: (a, b) => a.email?.localeCompare(b.email) },
    { title: 'Perfil', dataIndex: 'profile', key: 'profile', sorter: (a, b) => a.profile?.localeCompare(b.profile) },
    { title: 'Time ID', dataIndex: 'customer_id', key: 'customer_id' },
    { title: 'Status', dataIndex: 'status', key: 'status', sorter: (a, b) => a.status?.localeCompare(b.status) },
    { title: 'Criado em', dataIndex: 'created_at', key: 'created_at', render: (value) => value ? new Date(value).toLocaleString('pt-BR') : '' },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => showModal(record)}
            size="small"
          />
          {record.status === 'ATIVO' && (
            <Popconfirm
              title="Tem certeza que deseja inativar este usuário?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button icon={<DeleteOutlined />} danger size="small" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <Search
          className={mobileSearchClass}
          placeholder="Buscar usuários..."
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          style={{ width: isMobile ? '100%' : 300 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Novo Usuário
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        showSorterTooltip={false}
      />

      <Modal
        title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={window.innerWidth <= 768 ? '95vw' : 600}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="username" label="Nome de Usuário" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item 
            name="password" 
            label={editingUser ? "Nova Senha (deixe vazio para manter)" : "Senha"} 
            rules={editingUser ? [] : [{ required: true }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="profile" label="Perfil" rules={[{ required: true }]}>
            <Select onChange={(value) => {
              if (value !== 'ORGANIZADOR') {
                setAvailableTeams([]);
                setSelectedTeam(null);
                form.setFieldValue('customer_id', null);
              }
            }}>
              <Option value="SUPORTE">Suporte</Option>
              <Option value="ADMINISTRADOR">Administrador</Option>
              <Option value="ORGANIZADOR">Organizador</Option>
            </Select>
          </Form.Item>
          
          {form.getFieldValue('profile') === 'ORGANIZADOR' && (
            <>
              <Form.Item name="phone" label="Telefone do Time" rules={[{ required: true }]}>
                <Input 
                  placeholder="Digite o telefone do time"
                  onChange={(e) => searchTeamsByPhone(e.target.value)}
                />
              </Form.Item>
              
              {availableTeams.length > 0 && (
                <Form.Item label="Times Encontrados">
                  <List
                    size="small"
                    dataSource={availableTeams}
                    renderItem={(team) => (
                      <List.Item
                        actions={[
                          <Button 
                            type={selectedTeam === team.id ? 'primary' : 'default'}
                            size="small"
                            onClick={() => handleTeamSelect(team.id)}
                          >
                            {selectedTeam === team.id ? 'Selecionado' : 'Selecionar'}
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          title={team.game_name}
                          description={`ID: ${team.id} | Status: ${team.status}`}
                        />
                      </List.Item>
                    )}
                  />
                </Form.Item>
              )}
              
              <Form.Item name="customer_id" hidden>
                <Input />
              </Form.Item>
            </>
          )}
          
          {editingUser && (
            <Form.Item name="status" label="Status">
              <Select>
                <Option value="ATIVO">Ativo</Option>
                <Option value="INATIVO">Inativo</Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item style={{marginTop: 28}}>
            <Space>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;